import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { NavBar, Card, Button } from 'antd-mobile';
import { LeftOutline, LockOutline } from 'antd-mobile-icons';
import { useI18n } from '@/hooks/useI18n';

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { t } = useI18n();

  const privacySections = [
    {
      title: t('privacy.dataCollection'),
      content: t('privacy.dataCollectionContent')
    },
    {
      title: t('privacy.dataUsage'),
      content: t('privacy.dataUsageContent')
    },
    {
      title: t('privacy.dataSharing'),
      content: t('privacy.dataSharingContent')
    },
    {
      title: t('privacy.dataSecurity'),
      content: t('privacy.dataSecurityContent')
    },
    {
      title: t('privacy.userRights'),
      content: t('privacy.userRightsContent')
    },
    {
      title: t('privacy.cookies'),
      content: t('privacy.cookiesContent')
    },
    {
      title: t('privacy.thirdParty'),
      content: t('privacy.thirdPartyContent')
    },
    {
      title: t('privacy.policyChanges'),
      content: t('privacy.policyChangesContent')
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex justify-center">
      <div className="w-full min-h-screen relative overflow-hidden" style={{ maxWidth: '480px' }}>
      {/* 背景装饰 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-cyan-400/10 to-blue-400/10 rounded-full blur-3xl" />
      </div>

      {/* 顶部导航 */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <NavBar 
          onBack={() => navigate('/profile')}
          className="bg-white/80 backdrop-blur-xl border-b border-gray-200/50"
        >
          <div className="flex items-center text-gray-800">
            <LockOutline className="mr-2" />
            {t('settings.privacyPolicy')}
          </div>
        </NavBar>
      </motion.div>

      <div className="p-4 relative z-10">
        {/* 隐私政策标题 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <motion.div 
              className="p-8 text-center"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3, type: "spring", stiffness: 200 }}
                className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
              >
                <LockOutline className="text-white text-2xl" />
              </motion.div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                {t('privacy.title')}
              </h1>
              <p className="text-gray-500 text-sm">
                {t('privacy.lastUpdated')}: 2024年1月1日
              </p>
            </motion.div>
          </Card>
        </motion.div>

        {/* 隐私政策内容 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mt-4"
        >
          <Card className="bg-white/80 backdrop-blur-xl border-0 shadow-xl rounded-2xl overflow-hidden">
            <div className="p-6">
              <motion.div 
                className="mb-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {t('privacy.introduction')}
                  </p>
                </div>
              </motion.div>

              <div className="space-y-6">
                {privacySections.map((section, index) => (
                  <motion.div 
                    key={index}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.4 + index * 0.1 }}
                    whileHover={{ x: 4 }}
                    className="p-4 bg-gradient-to-r from-gray-50/50 to-gray-100/50 rounded-xl border border-gray-200 hover:border-blue-200 transition-all duration-200"
                  >
                    <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                      <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mr-3 text-white text-sm font-bold">
                        {index + 1}
                      </div>
                      {section.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed pl-9">
                      {section.content}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* 重要提示 */}
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.8 }}
                className="mt-8 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200"
              >
                <h4 className="text-base font-semibold text-orange-800 mb-2 flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-yellow-500 to-orange-500 rounded-full mr-3" />
                  {t('privacy.importantNotice')}
                </h4>
                <p className="text-orange-700 text-sm leading-relaxed">
                  {t('privacy.importantNoticeContent')}
                </p>
              </motion.div>

              {/* 联系方式 */}
              <motion.div 
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.9 }}
                className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200"
              >
                <h4 className="text-base font-semibold text-gray-800 mb-2 flex items-center">
                  <div className="w-1 h-5 bg-gradient-to-b from-gray-500 to-slate-500 rounded-full mr-3" />
                  {t('privacy.contactInfo')}
                </h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {t('privacy.contactDetails')}
                </p>
              </motion.div>
            </div>
          </Card>
        </motion.div>

        {/* 操作按钮 */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, delay: 1.0 }}
          className="mt-6"
        >
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              color="primary"
              block
              onClick={() => navigate('/profile')}
              className="bg-gradient-to-r from-blue-500 to-indigo-500 border-0 rounded-xl h-12 font-medium shadow-lg"
            >
              {t('common.back')}
            </Button>
          </motion.div>
        </motion.div>

        {/* 底部间距 */}
        <div className="h-8" />
      </div>
      </div>
    </div>
  );
}